import {
  ErrorModal,
  HelmetDexteel,
  ShiftDayNavigatorControl,
  useShiftNavigator,
} from "@dexteel/mesf-core";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ExpandableSearch from "../../../shared/selectors/ExpandableSearch";
import { Heat } from "../models/Heat";
import { HeatsByShiftTable } from "./components/HeatsByShiftTable";
import { getHeatsByShift } from "./repositories/heatsRepository";

export const MeltshopHeats = () => {
  const navigate = useNavigate();
  const { shiftId: shiftIdParam } = useParams();
  const [params] = useSearchParams();
  const [heatSelected, setHeatSelected] = useState<Heat | null>(null);

  const [heatsByShift, setHeatsByShift] = useState<any[]>([]);

  const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();

  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showDeletedHeats, setShowDeletedHeats] = useState(false);

  const getHeatsByShiftFromAPI = async () => {
    setIsLoading(true);
    if (shiftInfo) {
      const resp = await getHeatsByShift(
        shiftInfo.CurrentShiftId,
        showDeletedHeats,
      );
      if (resp.ok) {
        setHeatsByShift(get(resp, "data.tables[0].rows", []));
      } else {
        setError(resp.message);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (shiftInfo) {
      const shiftId = get(shiftInfo, "CurrentShiftId", null);
      if (heatSelected && heatSelected.HeatName !== "")
        navigate(
          `/meltshop-heats/${shiftId}?heatName=${heatSelected.HeatName}`,
        );
      else navigate(`/meltshop-heats/${shiftId}`);
      (async () => {
        await getHeatsByShiftFromAPI();
      })();
    }
  }, [shiftInfo, heatSelected, showDeletedHeats]);

  useEffect(() => {
    if (shiftIdParam) {
      (async () => {
        const shiftId = Number(shiftIdParam);
        await getShiftDataFromAPI(null, shiftId);
      })();
    } else {
      (async () => {
        await getShiftDataFromAPI(null, 0);
      })();
    }
    const heatName = params.get("heatName");
    if (heatName && heatsByShift.length > 0) {
      const foundHeat =
        heatsByShift.find((heat: Heat) => heat.HeatName === heatName) || null;
      setHeatSelected(foundHeat);
    }
  }, []);

  return (
    <>
      <HelmetDexteel
        title={`${shiftInfo?.CurrentStart.toLocaleDateString()} - Heats`}
      />
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Heats
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }} elevation={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm="auto">
                <ShiftDayNavigatorControl
                  preventInit
                  withPaper={false}
                  margin="0"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <ExpandableSearch
                  heatSelected={heatSelected}
                  setHeatSelected={setHeatSelected}
                />
              </Grid>
              <Grid item sx={{ width: "fit-content", ml: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showDeletedHeats}
                      color="primary"
                      onChange={(event) =>
                        setShowDeletedHeats(event.target.checked)
                      }
                    />
                  }
                  label="Show deleted heats"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <HeatsByShiftTable
            isLoading={isLoading}
            getHeatsByShift={getHeatsByShiftFromAPI}
            heatsByShift={heatsByShift}
            heatSelected={heatSelected}
            showDeletedHeats={showDeletedHeats}
          />
        </Grid>
      </Grid>
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
